import { type FC, useState, useEffect, useRef, useCallback } from "react";
import { usePouch, useView } from "use-pouchdb";
import * as uuid from "uuid";

import { assertIsItemAttachmentDoc } from "../common/schema";
import { getImageClass } from "./image_attachments";
import { assert } from "./utils";
import { type ExistingItemAttachmentDoc } from "./existing_docs";
import { generateThumbnail, backfillThumbnail } from "./thumbnails";

interface PendingAttachment {
  file: File;
  previewUrl: string | null; // null for non-image files
  caption: string;
}

const thumbnailBoxStyle: React.CSSProperties = {
  width: 80, height: 80, flexShrink: 0,
  backgroundColor: "#ddd",
  display: "flex", alignItems: "center", justifyContent: "center",
};

const AttachmentCaptionRow: FC<{
  doc: ExistingItemAttachmentDoc,
  left: React.ReactNode,
  extraButtons?: React.ReactNode,
  isPrimary?: boolean,
  onTogglePrimary?: () => void,
}> = ({doc, left, extraButtons, isPrimary, onTogglePrimary}) => {
  const db = usePouch();
  const [caption, setCaption] = useState(doc.caption);
  const dirty = caption !== doc.caption;

  const saveCaption = () => {
    const updated = {...doc, caption};
    db.put(updated).catch(console.error);
  };

  const softDelete = () => {
    const withDeletion = {...doc, deleted_at: new Date().toISOString()};
    db.put(withDeletion).catch(console.error);
  };

  // Keeps the input in sync when doc.caption changes due to live replication from
  // another client; without this, an external update would leave a spurious Save button.
  useEffect(() => { setCaption(doc.caption); }, [doc.caption]);

  return <div data-testid="attachment-caption-row" style={{display: "flex", flexDirection: "row", alignItems: "center", gap: "4px", margin: "2px 0"}}>
    {left}
    <input
      type="text"
      value={caption}
      onChange={e => setCaption(e.target.value)}
      className={dirty ? "" : "input-unchanged"}
      placeholder="caption"
      style={{flexGrow: 1}}
    />
    {dirty && <button type="button" onClick={saveCaption}>Save</button>}
    {onTogglePrimary && <button type="button" onClick={onTogglePrimary} title={isPrimary ? "Unset primary" : "Set as primary image"} style={{opacity: isPrimary ? 1 : 0.4}}>{isPrimary ? "★" : "☆"}</button>}
    {extraButtons}
    <button type="button" onClick={softDelete}>Delete</button>
  </div>;
};

const ImageCard: FC<{doc: ExistingItemAttachmentDoc; isPrimary: boolean; onTogglePrimary: () => void}> = ({doc, isPrimary, onTogglePrimary}) => {
  const db = usePouch();
  const [caption, setCaption] = useState(doc.caption);
  const dirty = caption !== doc.caption;
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  useEffect(() => { setCaption(doc.caption); }, [doc.caption]);

  useEffect(() => () => {
    if (lightboxUrl != null) URL.revokeObjectURL(lightboxUrl);
  }, [lightboxUrl]);

  useEffect(() => {
    if (lightboxUrl == null) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") setLightboxUrl(null); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxUrl]);

  useEffect(() => {
    backfillThumbnail(db, doc).catch(console.error);
  }, [db, doc._id, doc._rev]);

  const openLightbox = () => {
    db.getAttachment(doc._id, "file", {rev: doc._rev}).then(attachment => {
      assert(attachment instanceof Blob);
      setLightboxUrl(URL.createObjectURL(attachment));
    }).catch(console.error);
  };

  const saveCaption = () => {
    const updated = {...doc, caption};
    db.put(updated).catch(console.error);
  };

  const softDelete = () => {
    const withDeletion = {...doc, deleted_at: new Date().toISOString()};
    db.put(withDeletion).catch(console.error);
  };

  const imageClass = getImageClass(db, doc, "file");

  return <>
    {lightboxUrl != null && (
      <div
        data-testid="image-lightbox"
        onClick={() => setLightboxUrl(null)}
        style={{
          position: "fixed", inset: 0, zIndex: 1000,
          backgroundColor: "rgba(0,0,0,0.85)",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "zoom-out",
        }}
      >
        <img src={lightboxUrl} style={{maxWidth: "95vw", maxHeight: "95vh", objectFit: "contain"}} />
      </div>
    )}
    <div data-testid="attachment-caption-row" style={{display: "flex", flexDirection: "column", gap: "4px"}}>
      <div style={{position: "relative"}}>
        <div
          className={imageClass ?? ""}
          data-testid="image-thumbnail"
          onClick={openLightbox}
          style={{
            width: "100%",
            aspectRatio: "1",
            backgroundImage: "var(--image)",
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundColor: "#ddd",
            cursor: "zoom-in",
          }}
        />
        <button
          type="button"
          onClick={onTogglePrimary}
          title={isPrimary ? "Unset primary" : "Set as primary image"}
          style={{
            position: "absolute", top: "4px", right: "4px",
            opacity: isPrimary ? 1 : 0.6,
            background: "rgba(255,255,255,0.8)",
            border: "none", borderRadius: "4px",
            cursor: "pointer", fontSize: "16px", padding: "2px 4px",
          }}
        >{isPrimary ? "★" : "☆"}</button>
      </div>
      <input
        type="text"
        value={caption}
        onChange={e => setCaption(e.target.value)}
        className={dirty ? "" : "input-unchanged"}
        placeholder="caption"
        style={{width: "100%", boxSizing: "border-box"}}
      />
      <div style={{display: "flex", gap: "4px", justifyContent: "flex-end"}}>
        {dirty && <button type="button" onClick={saveCaption}>Save</button>}
        <button type="button" onClick={softDelete}>Delete</button>
      </div>
    </div>
  </>;
};

const FileAttachmentRow: FC<{doc: ExistingItemAttachmentDoc}> = ({doc}) => {
  const db = usePouch();
  const contentType = doc._attachments?.["file"]?.content_type ?? "";
  const typeLabel = contentType.split("/")[1]?.toUpperCase() ?? "FILE";

  const download = () => {
    db.getAttachment(doc._id, "file", {rev: doc._rev}).then(blob => {
      assert(blob instanceof Blob);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.caption || typeLabel.toLowerCase();
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }).catch(console.error);
  };

  return <AttachmentCaptionRow doc={doc}
    left={<div style={{...thumbnailBoxStyle, fontFamily: "monospace", fontSize: "12px", fontWeight: "bold"}}>{typeLabel}</div>}
    extraButtons={<button type="button" onClick={download}>↓</button>}
  />;
};

const PendingImageCard: FC<{
  pending: PendingAttachment,
  onSave: () => void,
  onCancel: () => void,
  onCaptionChange: (c: string) => void,
}> = ({pending, onSave, onCancel, onCaptionChange}) => (
  <div data-testid="pending-attachment-row" style={{display: "flex", flexDirection: "column", gap: "4px", border: "1px dashed #aaa", padding: "4px"}}>
    <img src={pending.previewUrl ?? ""} style={{width: "100%", aspectRatio: "1", objectFit: "contain", backgroundColor: "#ddd"}} />
    <input
      type="text"
      value={pending.caption}
      onChange={e => onCaptionChange(e.target.value)}
      placeholder="caption (e.g. underside, product page, manual)"
      style={{width: "100%", boxSizing: "border-box"}}
      autoFocus
    />
    <div style={{display: "flex", gap: "4px", justifyContent: "flex-end"}}>
      <button type="button" onClick={onSave}>Save</button>
      <button type="button" onClick={onCancel}>Cancel</button>
    </div>
  </div>
);

const PendingRow: FC<{
  pending: PendingAttachment,
  onSave: () => void,
  onCancel: () => void,
  onCaptionChange: (c: string) => void,
}> = ({pending, onSave, onCancel, onCaptionChange}) => (
  <div data-testid="pending-attachment-row" style={{display: "flex", flexDirection: "row", alignItems: "center", gap: "4px", margin: "4px 0", padding: "4px", border: "1px dashed #aaa"}}>
    <div style={{...thumbnailBoxStyle, fontFamily: "monospace", fontSize: "12px", fontWeight: "bold"}}>
      {(pending.file.type.split("/")[1] ?? "FILE").toUpperCase()}
    </div>
    <input
      type="text"
      value={pending.caption}
      onChange={e => onCaptionChange(e.target.value)}
      placeholder="caption (e.g. underside, product page, manual)"
      style={{flexGrow: 1}}
      autoFocus
    />
    <button type="button" onClick={onSave}>Save</button>
    <button type="button" onClick={onCancel}>Cancel</button>
  </div>
);

export const ItemAttachments: FC<{itemId: string}> = ({itemId}) => {
  const db = usePouch();
  const { rows } = useView("built/attachments_by_item", { key: itemId, include_docs: true });
  const [pending, setPending] = useState<PendingAttachment | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const imageRows: ExistingItemAttachmentDoc[] = [];
  const fileRows: ExistingItemAttachmentDoc[] = [];
  for (const row of rows) {
    assertIsItemAttachmentDoc(row.doc);
    const ct = row.doc._attachments?.["file"]?.content_type ?? "";
    (ct.startsWith("image/") ? imageRows : fileRows).push(row.doc);
  }

  // useCallback so the effect dep is stable (setPending is stable from useState)
  const startPending = useCallback((file: File) => {
    const previewUrl = file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
    setPending({ file, previewUrl, caption: "" });
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file == null) return;
    startPending(file);
    e.target.value = "";
  };

  // Keep pending in a ref so the drop handler always sees the latest value
  // without needing to re-attach listeners on every render.
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onDragEnter = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      setIsDragging(true);
    };
    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      setIsDragging(true);
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (pendingRef.current != null) return;
      let file: File | undefined = e.dataTransfer?.files[0];
      if (file == null && e.dataTransfer?.items != null) {
        const item = Array.from(e.dataTransfer.items).find(i => i.kind === "file");
        file = item?.getAsFile() ?? undefined;
      }
      if (file != null) startPending(file);
    };
    const onDragLeave = (e: DragEvent) => {
      if (!(e.relatedTarget instanceof Node) || !el.contains(e.relatedTarget)) setIsDragging(false);
    };

    // {passive: false} ensures preventDefault() is never silently ignored,
    // which can happen when React's synthetic event delegation is in play.
    el.addEventListener("dragenter", onDragEnter, { passive: false });
    el.addEventListener("dragover", onDragOver, { passive: false });
    el.addEventListener("drop", onDrop);
    el.addEventListener("dragleave", onDragLeave);
    return () => {
      el.removeEventListener("dragenter", onDragEnter);
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("drop", onDrop);
      el.removeEventListener("dragleave", onDragLeave);
    };
  }, [startPending]);

  const savePending = async () => {
    if (pending == null) return;
    const attachId = `item_attachment:${uuid.v7()}`;
    const isFirstImage = pending.file.type.startsWith("image/") && imageRows.length === 0;

    const thumbBlob = pending.previewUrl != null
      ? await generateThumbnail(pending.previewUrl, 200)
      : null;

    const pendingDoc = {
      _id: attachId,
      item_id: itemId,
      caption: pending.caption,
      deleted_at: null,
      ...(isFirstImage ? { primary: true } : {}),
      _attachments: {
        file: {
          content_type: pending.file.type || "application/octet-stream",
          data: pending.file,
        },
        ...(thumbBlob != null ? { thumbnail: { content_type: "image/jpeg", data: thumbBlob } } : {}),
      },
    };
    db.put(pendingDoc).then(() => {
      if (pending.previewUrl != null) URL.revokeObjectURL(pending.previewUrl);
      setPending(null);
    }).catch(console.error);
  };

  const cancelPending = () => {
    if (pending?.previewUrl != null) URL.revokeObjectURL(pending.previewUrl);
    setPending(null);
  };

  const pendingIsImage = pending != null && pending.file.type.startsWith("image/");

  const togglePrimary = (doc: ExistingItemAttachmentDoc) => {
    const makingPrimary = !doc.primary;
    // Clear existing primary if switching to a different one
    for (const row of imageRows) {
      if (row._id !== doc._id && row.primary) {
        db.put({...row, primary: false}).catch(console.error);
      }
    }
    db.put({...doc, primary: makingPrimary}).catch(console.error);
  };

  return <div
    ref={containerRef}
    data-testid="attachments-drop-zone"
    style={{marginTop: "8px", ...(isDragging ? {outline: "2px dashed #888", borderRadius: "4px", padding: "4px"} : {})}}
  >
    <strong>Images</strong>
    <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "8px", marginTop: "4px"}}>
      {pendingIsImage && pending != null && <PendingImageCard pending={pending} onSave={savePending} onCancel={cancelPending} onCaptionChange={c => setPending({...pending, caption: c})} />}
      {imageRows.map(doc => <ImageCard key={doc._id} doc={doc} isPrimary={doc.primary === true} onTogglePrimary={() => togglePrimary(doc)} />)}
    </div>

    <strong>Attachments</strong>
    {!pendingIsImage && pending != null && <PendingRow pending={pending} onSave={savePending} onCancel={cancelPending} onCaptionChange={c => setPending({...pending, caption: c})} />}
    {fileRows.map(doc => <FileAttachmentRow key={doc._id} doc={doc} />)}

    <div style={{marginTop: "4px", display: "flex", gap: "4px"}}>
      <input ref={fileInputRef} type="file" onChange={handleFileChange} style={{display: "none"}} />
      <button type="button" onClick={() => fileInputRef.current?.click()}>Add</button>
    </div>
  </div>;
};
